import React, { useState, useEffect } from "react";
import { Modal, Backdrop, Fade, Box, Grid, Tooltip, tooltipClasses } from '@mui/material';
import { makeStyles } from "@mui/styles";
import { styled } from '@mui/material/styles';

import { UseIsAdmin, UseUserDetails, UseIsPrime, UseCompanyId, UseUserId } from "../../hooks/auth";
import {
  UseCompanyLogo,
  UseCompanyName,
  UseCompanyIndustry,
  UseSelectedWorkspace,
} from "../../hooks/company";
import SoftTypography from "../SoftTypography";
import SoftBox from "../SoftBox";
import "./heroStyles.css";
import Avatar from "../Avatar";
import DynamicComponent, { Components } from "../../pages/Widgets/components";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ReactComponent as BookIcon } from "../../img/playbook-icon.svg";
import SoftButton from "../SoftButton";
import { useNavigate } from "react-router-dom";
import PageHeaderBg from "../../img/gamesettings/mainSection-min.png";
import PageHeaderBg2 from "../../img/gamesettings/truckBg.png";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from '@mui/icons-material/Close';
import { SUBMIT_BGIMAGE, GET_BACKGROUND } from "../../api/group";
import { FETCH_QUOTES } from "../../api/quotes";
import { useDispatch } from "react-redux";
import { refetchUserData } from "../../redux/auth/actions";
import { TOGGLE_ON_WIDGET, TOGGLE_OFF_WIDGET, ADD_WIDGETS_TO_LIST } from "../../api/userWidgets";
import toast from "react-hot-toast";
import GmbWelcomeLogo from "../../img/dashboard-welcomegmb.png";
import { borderRadius, minWidth } from "@mui/system";

const CustomWidthTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 425,
    background: 'linear-gradient(180deg, #101828 0%, #39558E 100%)',
    boxShadow: '0px 12px 24px rgba(0, 0, 0, 0.25)',
    color: '#101828',
  },
});

const CustomTooltipContent = React.forwardRef(({ closeTooltip, onNextTooltip }, ref) => (
  <SoftBox ref={ref} sx={{ p: 2, textAlign: 'left !important' }}>
    <SoftTypography variant="h6" component="h3" sx={{ mb: 1, fontWeight: 'bold', color: "#FFFFFF" }}>
      Widgets for Quick Insights
    </SoftTypography>
    <SoftTypography variant="body2" sx={{color: "#FFFFFF"}}>
      Widgets provide at-a-glance insights! Customize your<br/> dashboard with Widgets to monitor essential metrics and achievements.
    </SoftTypography>
    <SoftBox sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: "2rem" }}>
      <SoftTypography variant="body2" sx={{color: "#FFFFFF"}}>
        1/3
      </SoftTypography>
      <SoftBox sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <SoftTypography variant="body2" sx={{color: "#FFFFFF", marginRight: "0.5rem", cursor: "pointer"}} onClick={closeTooltip}>
          Skip Tutorial
        </SoftTypography>
        <SoftButton
          variant="outlined"
          onClick={() => {
            closeTooltip();
            onNextTooltip();
          }}
          sx={{
            background: "#FFFFFF",
            borderRadius: "4px",
            border: "1px solid #EAECF0",
            color: "#2152FF !important",
            textTransform: "capitalize !important",
            paddingTop: "4px",
            paddingBottom: "4px",
          }}
        >
          Next
        </SoftButton>
      </SoftBox>
    </SoftBox>
  </SoftBox>
));

const getTimeBasedGreeting = (time) => {
  if (!time) return "Hello";
  const hours = time.getHours();
  if (hours < 12) return "Good Morning";
  if (hours < 17) return "Good Afternoon";
  return "Good Evening";
};

const Hero = ({ triggerTooltip, onNextTooltip, closeTooltip}) => {
  const user = UseUserDetails();
  const logo = UseCompanyLogo();
  const companyName = UseCompanyName();
  const industry = UseCompanyIndustry();
  const isAdmin = UseIsAdmin();
  const workspace = UseSelectedWorkspace();
  const navigate = useNavigate();
  const [reloadKey, setReloadKey] = useState(0);

  const [userWidget, setUserWidget] = useState([]);
  const [quoteWidget, setQuoteWidget] = useState({});
  const [time, setTime] = useState(null);
  const xsBp = useMediaQuery((theme) => theme.breakpoints.up("xs"));
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const isMidsizeScreen = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  const screenWidth = window.innerWidth;

  const [openModal, setOpenModal] = useState(false);

  const dispatch = useDispatch();
  const userId = UseUserId();
  const companyId = UseCompanyId();

  const handleImageClick = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const [selectedImage, setSelectedImage] = useState(null);

  const handleImageSelect = (imageName) => {
    setSelectedImage(imageName);
  };

  const images = [
    { name: 'PageHeaderBg', src: PageHeaderBg },
    { name: 'PageHeaderBg2', src: PageHeaderBg2 },
  ];

  const { _id: groupId } = UseSelectedWorkspace();
  const [workspaceData, setWorkspaceData] = useState(null);
  const fetchData = async () => {
    try {
      const response = await GET_BACKGROUND(groupId);
      setWorkspaceData(response.data);  
    } catch (error) {
      console.error("Error fetching the award data:", error);
    }
  };
  
  useEffect(() => {
    fetchData();
  }, [groupId]);

  const submitImage = async () => {
    if (selectedImage) {
      try {
        const body = {
          groupId,
          backgroundImage: selectedImage,
        };
        await SUBMIT_BGIMAGE(body);
        fetchData();
        setOpenModal(false);
        setSelectedImage(null);
      } catch (error) {
        console.error("Error submitting background image:", error);
      }
    } else {
      console.warn("No image selected");
    }
  };

  const workspaceBg = workspaceData?.backgroundImage === 'primary' ? 'primary' : images.find(img => img.name === workspaceData?.backgroundImage)?.src || PageHeaderBg;

  const styleModal = {
    position: 'absolute',
    top: '35%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    background: 'linear-gradient(156.05deg, rgba(12, 12, 13, 0.5) 3.85%, rgba(12, 12, 13, 0.65) 50.21%, rgba(12, 12, 13, 0.51) 98.53%)',
    borderRadius: '10px',
    boxShadow: 24,
    padding: '1.75rem',
    maxWidth: { mini: "90%", xs: "90%", sm: "600px" },
    width: "100%",
    overflow: "visible",
    backdropFilter: 'blur(25px)'
  };

  const DefaultWidgets = [
    { Widget: Components.ranking, index: 0 }, // RankingComponent
    { Widget: Components.top_kpi, index: 1 }, // TopKPIComponent
    { Widget: Components.level, index: 2 } // LevelComponent
  ];

  useEffect(() => {
    // Real-time clock update
    const currentTime = new Date();
    const interval = setInterval(() => setTime(new Date()), 60 * 1000); // Update every minute only
    setTime(currentTime);
    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setReloadKey(Math.round(Math.random() * 1000));
    };

    // Window resize listener to refresh (and resize) avatar
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const fetchQuotesAndSetWidgets = async () => {
      if (user && user.widgets && user.widgets.length) {
        const userWidgets = [];
        let quoteData = null;

        const widgetPromises = user.widgets.map(async (widget) => {
          if (widget.key === "quote_of_the_day") {
            try {
              const response = await FETCH_QUOTES();
              quoteData = response.data;
            } catch (err) {
              console.log(err.response);
            }
          } else {
            userWidgets.push(widget);
          }
        });

        await Promise.all(widgetPromises);

        setUserWidget(userWidgets);

        if (quoteData && quoteData.length > 0) {
          setQuoteWidget(quoteData[0]);
        }
      }
    };

    fetchQuotesAndSetWidgets();
  }, [user]);

  const useStyles = makeStyles(() => ({
    topBoxbg: {
      filter: "drop-shadow(0px 1px 3px rgba(16, 24, 40, 0.1)) drop-shadow(0px 1px 2px rgba(16, 24, 40, 0.06))",
      paddingTop: { mini: "101.25px", xs: "109.25px", md: "75px" },
      // paddingBottom: "2rem",
      borderRadius: "12px",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      maxHeight: { mini: "960px", xxs: "1000px", md: "850px" },
      animation: "$bgAnimations 45s infinite alternate",
    },
    "@keyframes bgAnimations": {
      "0%": {
        backgroundPosition: "left",
      },
      "50%": {
        backgroundPosition: "right",
      },
      "100%": {
        backgroundPosition: "center center",
      },
    },
  }));

  const classes = useStyles();

  useEffect(() => {
    const fetchQuotesAndSetWidgets = async () => {
      if (user && user.widgets && user.widgets.length) {
        const userWidgets = [];
        let quoteData = null;

        const widgetPromises = user.widgets.map(async (widget) => {
          if (widget.key === "quote_of_the_day") {
            try {
              const response = await FETCH_QUOTES();
              quoteData = response.data;
            } catch (err) {
              console.log(err.response);
            }
          } else {
            userWidgets.push(widget);
          }
        });

        await Promise.all(widgetPromises);

        setUserWidget(userWidgets);

        if (quoteData && quoteData.length > 0) {
          setQuoteWidget(quoteData[0]);
        }
      }
    };

    fetchQuotesAndSetWidgets();
  }, [user]);

  const handleActivateQuote = async () => {
    try {
      const payload = {
        userId,
        companyId,
        key: "quote_of_the_day",
      };
  
      await TOGGLE_ON_WIDGET(payload);
      dispatch(refetchUserData(userId));
      setTimeout(() => {
        toast.success("Quote of the Day widget activated successfully");
      }, 1000);
  
      const response = await FETCH_QUOTES();
      setQuoteWidget(response.data[0]);
    } catch (error) {
      console.error("Error activating quote widget:", error);
      if (error.response?.data?.error?.message === "Ask your company admin to set this widget") {
        try {
          const quoteResponse = await FETCH_QUOTES();
          const quote = quoteResponse.data[0];
          
          const savePayload = {
            userId,
            key: "quote_of_the_day",
            category: "motivation", // Assuming the category is "motivation"
            title: "Quote of the Day",
            companyId,
          };
  
          await ADD_WIDGETS_TO_LIST(savePayload);
          dispatch(refetchUserData(userId));
          setTimeout(() => {
            setQuoteWidget(quote);
            toast.success("Quote of the Day widget activated successfully");
          }, 1000);
        } catch (saveError) {
          console.error("Error saving quote widget:", saveError);
          toast.error("Failed to save Quote of the Day widget");
        }
      } else {
        toast.error(error.response?.data?.error?.message || "Something went wrong!");
      }
    }
  };

  const handleDeactivateQuote = async () => {
    try {
      const quoteWidgets = user.widgets.find(widget => widget.key === "quote_of_the_day");
      if (!quoteWidgets) {
        console.error("Quote widget not found");
        return;
      }

      await TOGGLE_OFF_WIDGET(quoteWidgets._id, userId);
      dispatch(refetchUserData(userId));
      setQuoteWidget(null);
      toast.success("Quote of the Day widget deactivated successfully");
    } catch (error) {
      console.error("Error deactivating quote widget:", error);
      toast.error(
        error.response?.data?.error?.message || "Something went wrong!"
      );
    }
  };
  
  const [openTooltip, setOpenTooltip] = useState(false);

  useEffect(() => {
    if (triggerTooltip) {
      setOpenTooltip(true);
    }
  }, [triggerTooltip]);

  const hideTooltip = () => {
    setOpenTooltip(false);
    closeTooltip();
  }

  return (
    <Box>
      <Grid container>
        <Grid item mini={12} md={12} lg={9}>
          <SoftBox
            sx={{
              height: { mini: "736px", xxs: "736px", md: "625px" },
              padding: { mini: "0", md: "1.5rem" },
              paddingRight: { mini: "0", md: "32px" },
              paddingTop: { mini: "0", md: "32px" },
              ...(isSmallScreen && ({
                minHeight: "85vh",
                height: "100%",
                marginBottom: "16px",
              })),
            }}
          >
            <SoftBox className={classes.topBoxbg}
              sx={{
                background: workspaceBg === 'primary' ? (theme) => theme.palette.radialGradient : `url(${workspaceBg})`,
                backgroundRepeat: "no-repeat",
                borderRadius: "12px",
                ...(isSmallScreen && ({ filter: "none !important", borderRadius: "0px" })),
                ...(!workspaceBg.includes("mainSection-min") && {
                  backgroundSize: isSmallScreen ? "cover" : "100%",
                }) 
              }}
            >
              <div style={{
                background: workspaceBg === 'primary' ? 'none' : `linear-gradient(${isSmallScreen ? 155.85 : 100.85}deg, #F9FAFB 25.18%, rgba(247, 248, 249, 0.74) 40.61%, rgba(238, 239, 240, 0.47) 59.75%, rgba(249, 250, 252, 0) 94.73%)`,
                borderRadius: "12px",
                height: "inherit",
                ...(isSmallScreen && {
                    borderRadius: "0px",
                    display: "flex",
                    flexDirection: "column",
                  }),
                }}
              >
                <SoftBox sx={{ paddingLeft: "2rem" }}>
                  <SoftTypography
                    sx={{
                      background:
                        "radial-gradient(100% 784.37% at 0% 50%, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.9) 25%, #FFF 50%, rgba(255, 255, 255, 0.9) 75%, rgba(255, 255, 255, 0.3) 100%, rgba(255, 255, 255, 0.2) 100%)",
                      backgroundClip: "text",
                      textFillColor: workspaceBg === 'primary' ? 'white' : 'primary.main',
                      fontWeight: "bold",
                      fontSize: { mini: "24px", sm: "2rem", md: "2rem" },
                      marginTop: {
                        mini: "1rem",
                        md: "4.5rem",
                      },
                      lineHeight: "32px",
                    }}
                  >
                    {getTimeBasedGreeting(time)}, {user?.firstName ?? "Player"}
                  </SoftTypography>
                  {(!isSmallScreen)
                    ? <>
                        <SoftTypography sx={{ textFillColor: workspaceBg === 'primary' ? 'white' : 'primary.main', marginTop: "1rem", marginBottom: "1rem", fontSize: "15px", fontWeight: '500' }}>
                          <span style={{cursor: "pointer"}} onClick={() => {
                            if (isAdmin) {
                              navigate("/playbook-editor");
                            } else {
                              navigate("/playbook-viewer");
                            }
                          }}>{isAdmin ? "Upload Playbook" : "View Playbook"}</span>{isAdmin && (<> | <span style={{cursor: "pointer"}} onClick={() => navigate("/settings/players")}>Manage Players</span></>)}
                        </SoftTypography>
                        <SoftTypography sx={{ textFillColor: workspaceBg === 'primary' ? 'white' : 'primary.main', fontSize: '22px', lineHeight: '24px', fontWeight: '500' }}>
                          Hello Player
                        </SoftTypography>
                      </>
                    :  <SoftTypography
                          sx={{ textFillColor: workspaceBg === 'primary' ? 'white' : 'primary.main', fontWeight: '500', fontSize: "14px", color: "#667085" }}
                          variant="body2"
                        >
                          {time &&
                            time.toLocaleString("en-US", {
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                              hour: "numeric",
                              minute: "numeric",
                              hour12: true,
                            }) + " "}
                      </SoftTypography>
                  }
                </SoftBox>
                <SoftBox sx={{ position: "relative", ...(isSmallScreen && ({ marginTop: "20px" })) }}>
                  <SoftBox
                    sx={{
                      position: "absolute",
                      width: {
                        mini: "100vw",
                        md: "50vw",
                        lg: "50vw",
                        xl: "50vw"
                      },
                      marginTop: {
                        md: "-10.5rem",
                      },
                      height: {
                        mini: "58vh",
                        md: "515px",
                      },
                      right: 0
                    }}
                  >
                    <Avatar
                      key={`Avatar${reloadKey}`}
                      url={user.avatar}
                      logo={logo}
                      industry={industry}
                      noControls={true}
                    />

                {(isSmallScreen && quoteWidget?.q) &&
                  <SoftBox sx={{ position: "relative", padding: "20px", bottom: 0,
                      top: 0,
                      left: 0,
                      // transform: "translate(0, 50%)"
                  }}>
                    <SoftTypography
                      variant="body1"
                      sx={{
                        textFillColor: workspaceBg === 'primary' ? 'white' : '#1D2939',
                        maxWidth: "600px",
                        fontSize: "14px",
                        fontWeight: 500,
                        fontStyle: "normal",
                        lineHeight: "20px",
                        marginBottom: "8px",
                      }}
                    >
                      "{quoteWidget.q}"
                    </SoftTypography>
                    <SoftTypography
                      sx={{ textFillColor: workspaceBg === 'primary' ? 'white' : '#1D2939', fontSize: "16px", fontFamily: "Roboto", fontWeight: 400, lineHeight: "16px" }}
                      variant="body2"
                    >
                      {quoteWidget.a}
                    </SoftTypography>
                  </SoftBox>
                }
                  </SoftBox>
                </SoftBox>
                <Box
                  sx={{
                    marginX: "auto",
                    width: "100%",
                    paddingX: { mini: "0.5rem", md: "1rem", xl: "2rem" },
                    flex: 1,
                    display: "grid",
                    alignItems: "center",
                    bottom: 25,
                    position: "relative",
                  }}
                >
                  <SoftBox
                    sx={{
                      alignSelf: "end",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <SoftBox
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginTop: "13.7rem",
                        marginBottom: "2rem",
                        ...(isSmallScreen && ({
                          maxWidth: "100%",
                          display: "unset",
                          marginTop: "unset",
                          marginBottom: "unset"
                        })),
                        ...(screenWidth <= 1280 && ({
                          marginTop: "10rem!important"
                        }))
                      }}
                    >
                      {quoteWidget && quoteWidget.q ? (
                        !isSmallScreen &&
                        <SoftBox sx={{maxWidth: "45%"}}>
                          <SoftBox
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: { mini: "0.25rem", md: "0.5rem" },
                              marginTop: "2rem"
                            }}
                          >
                            <SoftTypography
                              variant={xsBp ? "h1" : "h2"}
                              sx={{
                                textFillColor: workspaceBg === 'primary' ? 'white' : 'primary.main',
                                maxWidth: "600px",
                                fontSize: "16px",
                                fontWeight: "500",
                                fontStyle: "normal",
                                lineHeight: "28px",
                              }}
                            >
                              "{quoteWidget.q}" - {quoteWidget.a}
                            </SoftTypography>
                          </SoftBox>
                          {(!isSmallScreen) &&
                            <SoftTypography
                              sx={{ textFillColor: workspaceBg === 'primary' ? 'white' : 'primary.main', fontWeight: '500', fontSize: "15px", marginTop: "1.5rem"}}
                              variant="body2"
                            >
                              {time &&
                                time.toLocaleString("en-US", {
                                  day: "numeric",
                                  month: "long",
                                  year: "numeric",
                                  hour: "numeric",
                                  minute: "numeric",
                                  hour12: true,
                                }) + " "}
                            </SoftTypography>
                          }
                        </SoftBox>
                      ) : (
                        <SoftBox sx={{marginTop: !isAdmin ? "6rem!important" : "5.65rem", marginBottom: "2rem"}}>
                          <SoftTypography
                            sx={{ textFillColor: workspaceBg === 'primary' ? 'white' : 'primary.main', fontWeight: '500', fontSize: "15px" }}
                            variant="body2"
                          >
                            {(!isSmallScreen && time) &&
                              time.toLocaleString("en-US", {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                                hour: "numeric",
                                minute: "numeric",
                                hour12: true,
                              }) + " "
                            }
                            {workspace?.name ?? companyName}
                          </SoftTypography>
                        </SoftBox>
                      )}

                      {(!isSmallScreen) && (
                        <>
                          <SoftButton
                            variant="contained"
                            sx={{
                              background: (theme) => theme.palette.tertiary.dark,
                              height: "max-content",
                              width: "max-content",
                              minWidth: "max-content",
                              minHeight: "max-content",
                              borderRadius: "50%",
                              padding: "0.5rem",
                              marginTop: "4rem",
                              "&:hover": {
                                background: (theme) => theme.palette.tertiary.dark,
                              },
                              "&:focus:not(:hover)": {
                                background: (theme) => theme.palette.tertiary.dark,
                              },
                            }}
                            onClick={() => {
                              if (isAdmin) {
                                navigate("/playbook-editor");
                              } else {
                                navigate("/playbook-viewer");
                              }
                            }}
                          >
                            <BookIcon />
                          </SoftButton>
                          {(isAdmin) && (
                          <>
                          <SoftButton
                            variant="contained"
                            sx={{
                              background: 'linear-gradient(180deg, rgba(52, 64, 84, 0.25) 0%, rgba(115, 142, 186, 0.25) 100%)',
                              border: '1px solid rgba(102, 112, 133, 0.2)',
                              boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.16), inset 4px 0px 4px rgba(83, 81, 81, 0.25), inset 0px 4px 4px rgba(0, 0, 0, 0.25)',
                              height: "max-content",
                              width: "max-content",
                              minWidth: "max-content",
                              minHeight: "max-content",
                              borderRadius: "50%",
                              padding: "0.5rem",
                              top: "-8rem!important",
                              position: 'absolute',
                              right: '2rem!important',
                              "&:hover": {
                                background: (theme) => theme.palette.tertiary.dark,
                              },
                              "&:focus:not(:hover)": {
                                background: (theme) => theme.palette.tertiary.dark,
                              },
                            }}
                            onClick={isAdmin ? handleImageClick : undefined}
                            >
                            <svg width="31" height="30" viewBox="0 0 31 30" fill="none" xmlns="http://www.w3.org/2000/svg" style={{zIndex: "1000"}}>
                              <g clipPath="url(#clip0_35_4281)">
                              <path d="M7.42498 24.3999C6.66248 24.3999 5.94998 24.1374 5.43748 23.6499C4.78748 23.0374 4.47498 22.1124 4.58748 21.1124L5.04998 17.0624C5.13748 16.2999 5.59998 15.2874 6.13748 14.7374L16.4 3.87494C18.9625 1.16244 21.6375 1.08744 24.35 3.64994C27.0625 6.21244 27.1375 8.88744 24.575 11.5999L14.3125 22.4624C13.7875 23.0249 12.8125 23.5499 12.05 23.6749L8.02498 24.3624C7.81248 24.3749 7.62498 24.3999 7.42498 24.3999ZM20.4125 3.63744C19.45 3.63744 18.6125 4.23744 17.7625 5.13744L7.49998 16.0124C7.24998 16.2749 6.96248 16.8999 6.91248 17.2624L6.44998 21.3124C6.39998 21.7249 6.49998 22.0624 6.72498 22.2749C6.94998 22.4874 7.28748 22.5624 7.69998 22.4999L11.725 21.8124C12.0875 21.7499 12.6875 21.4249 12.9375 21.1624L23.2 10.2999C24.75 8.64994 25.3125 7.12494 23.05 4.99994C22.05 4.03744 21.1875 3.63744 20.4125 3.63744Z" fill="#EAECF0"/>
                              <path d="M22.1751 13.6876C22.1501 13.6876 22.1126 13.6876 22.0876 13.6876C18.1876 13.3001 15.0501 10.3376 14.4501 6.46256C14.3751 5.95006 14.7251 5.47506 15.2376 5.38756C15.7501 5.31256 16.2251 5.66256 16.3126 6.17506C16.7876 9.20006 19.2376 11.5251 22.2876 11.8251C22.8001 11.8751 23.1751 12.3376 23.1251 12.8501C23.0626 13.3251 22.6501 13.6876 22.1751 13.6876Z" fill="#EAECF0"/>
                              <path d="M26.75 28.4375H4.25C3.7375 28.4375 3.3125 28.0125 3.3125 27.5C3.3125 26.9875 3.7375 26.5625 4.25 26.5625H26.75C27.2625 26.5625 27.6875 26.9875 27.6875 27.5C27.6875 28.0125 27.2625 28.4375 26.75 28.4375Z" fill="#EAECF0"/>
                              </g>
                              <defs>
                              <clipPath id="clip0_35_4281">
                              <rect width="30" height="30" fill="white" transform="translate(0.5)"/>
                              </clipPath>
                              </defs>
                            </svg>
                          </SoftButton>
                          <Modal
                            open={openModal}
                            onClose={handleCloseModal}
                            BackdropComponent={Backdrop}
                            BackdropProps={{
                              timeout: 500,
                            }}
                          >
                            <Fade in={openModal}>
                              <SoftBox sx={styleModal}>
                                <SoftBox
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                >
                                  <SoftTypography sx={{textFillColor: "#ffffff"}} variant="h6" component="h2">
                                    Select your Dashboard Theme 
                                  </SoftTypography>
                                  <SoftButton
                                    variant="outlined"
                                    color="white"
                                    onClick={handleCloseModal}
                                    sx={{
                                      "& svg": { fontSize: "1.25rem !important" },
                                      borderRadius: "50%",
                                      minWidth: "max-content",
                                      minHeight: "max-content",
                                      padding: "0.25rem",
                                    }}
                                  >
                                    <CloseIcon />
                                  </SoftButton>
                                </SoftBox>
                                <hr style={{marginTop: '1rem'}}/>
                                <SoftBox sx={{marginTop: '1rem'}}>
                                  <form>
                                    <Grid container style={{
                                      background: 'rgba(255, 255, 255, 0.1)',
                                      border: '1px solid #667085',
                                      borderRadius: '8px',
                                      padding: '1rem'
                                    }}>
                                      {images.map((image) => (
                                        <Grid item mini={12} md={4} lg={4} key={image.name} style={{padding: '.25rem'}}>
                                          <label style={{
                                            border: selectedImage === image.name ? '2px solid #FFFFFF' : 'none',
                                            display: 'block',
                                            cursor: 'pointer',
                                            borderRadius: '8px'
                                          }}>
                                            <input
                                              type="radio"
                                              name="imageselect"
                                              value={image.name}
                                              style={{display: 'none'}}
                                              checked={selectedImage === image.name}
                                              onChange={() => handleImageSelect(image.name)}
                                            />
                                            <img src={image.src} alt="" style={{width: '100%', borderRadius: '8px'}}/>
                                          </label>
                                        </Grid>
                                      ))}
                                      <Grid item mini={12} md={4} lg={4} style={{padding: '.25rem'}}>
                                        <label style={{
                                          border: selectedImage === 'primary' ? '2px solid #FFFFFF' : 'none', 
                                          display: 'block',
                                          cursor: 'pointer',
                                          borderRadius: '8px'
                                        }}>
                                          <input
                                            type="radio"
                                            name="imageselect"
                                            value="primary"
                                            style={{display: 'none'}}
                                            checked={selectedImage === 'primary'}
                                            onChange={() => handleImageSelect('primary')}
                                          />
                                          <SoftBox sx={{
                                            width: '100%',
                                            height: '108px',
                                            borderRadius: '8px',
                                            background: (theme) => theme.palette.radialGradient
                                          }} />
                                        </label>
                                      </Grid>
                                    </Grid>
                                  </form>
                                  <SoftBox style={{
                                      background: 'rgba(255, 255, 255, 0.1)',
                                      border: '1px solid #667085',
                                      borderRadius: '8px',
                                      padding: '1rem',
                                      marginTop: '1rem',
                                    }}>
                                      <SoftTypography sx={{fontSize: '20px', fontWeight: '500', color: '#ffffff', marginBottom: '0.5rem'}}>
                                        Quotes
                                      </SoftTypography>
                                      <SoftTypography sx={{fontSize: '14px', fontWeight: '500', color: '#ffffff', marginBottom: '1rem'}}>
                                        Do you want your Team getting a daily quotes to keep them motivated for their daily task?
                                      </SoftTypography>
                                      {quoteWidget && quoteWidget.q ? (
                                        !isSmallScreen &&
                                        <SoftBox>
                                          <SoftButton
                                            variant="outlined"
                                            size="small"
                                            sx={{
                                              "& svg": { fontSize: "1.25rem !important" },
                                              background: "linear-gradient(180deg, rgba(255, 255, 255, 0.5) 4.41%, rgba(237, 236, 242, 0.5) 94.12%)",
                                              borderRadius: "4px",
                                              border: "1px solid #EAECF0",
                                              padding: "10px 16px",
                                              color: workspaceBg === 'primary' ? 'white !important' : '#667085 !important',
                                              textTransform: "capitalize !important",
                                              marginTop: '0.25rem',
                                              zIndex: "1000!important",
                                              cursor: "pointer"
                                            }}
                                            onClick={handleDeactivateQuote}
                                          >
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path fillRule="evenodd" clipRule="evenodd" d="M7.8523 2.91387C7.90737 2.982 7.94846 3.06033 7.97324 3.14436C7.99802 3.22839 8.006 3.31648 7.99671 3.40359C7.98742 3.49071 7.96106 3.57514 7.91912 3.65206C7.87718 3.72897 7.82049 3.79687 7.7523 3.85187C7.37963 4.1532 7.1363 4.44253 6.97696 4.70253C7.22467 4.76186 7.4502 4.89091 7.62687 5.07439C7.80354 5.25788 7.92396 5.48813 7.97387 5.73791C8.02379 5.98768 8.00111 6.24653 7.90853 6.48382C7.81595 6.72112 7.65733 6.92692 7.45145 7.0769C7.24557 7.22687 7.00104 7.31474 6.74679 7.3301C6.49254 7.34546 6.23921 7.28767 6.01677 7.16357C5.79433 7.03947 5.6121 6.85425 5.49162 6.62983C5.37115 6.4054 5.31748 6.15117 5.33696 5.8972C5.32096 5.5692 5.3503 5.13053 5.5323 4.63587C5.74963 4.04653 6.16963 3.41653 6.9143 2.81453C6.98243 2.75946 7.06076 2.71837 7.14479 2.69359C7.22882 2.66881 7.31691 2.66083 7.40402 2.67012C7.49114 2.67941 7.57557 2.70577 7.65249 2.74771C7.7294 2.78965 7.7973 2.84634 7.8523 2.91453V2.91387ZM10.0003 3.3332C9.82349 3.3332 9.65392 3.40344 9.52889 3.52846C9.40387 3.65349 9.33363 3.82306 9.33363 3.99987C9.33363 4.17668 9.40387 4.34625 9.52889 4.47127C9.65392 4.5963 9.82349 4.66653 10.0003 4.66653H13.3336C13.5104 4.66653 13.68 4.5963 13.805 4.47127C13.9301 4.34625 14.0003 4.17668 14.0003 3.99987C14.0003 3.82306 13.9301 3.65349 13.805 3.52846C13.68 3.40344 13.5104 3.3332 13.3336 3.3332H10.0003ZM10.0003 5.99987C9.82349 5.99987 9.65392 6.0701 9.52889 6.19513C9.40387 6.32015 9.33363 6.48972 9.33363 6.66653C9.33363 6.84334 9.40387 7.01291 9.52889 7.13794C9.65392 7.26296 9.82349 7.3332 10.0003 7.3332H13.3336C13.5104 7.3332 13.68 7.26296 13.805 7.13794C13.9301 7.01291 14.0003 6.84334 14.0003 6.66653C14.0003 6.48972 13.9301 6.32015 13.805 6.19513C13.68 6.0701 13.5104 5.99987 13.3336 5.99987H10.0003ZM2.66696 9.3332C2.66696 9.15639 2.7372 8.98682 2.86223 8.8618C2.98725 8.73677 3.15682 8.66653 3.33363 8.66653H13.3336C13.5104 8.66653 13.68 8.73677 13.805 8.8618C13.9301 8.98682 14.0003 9.15639 14.0003 9.3332C14.0003 9.51001 13.9301 9.67958 13.805 9.8046C13.68 9.92963 13.5104 9.99987 13.3336 9.99987H3.33363C3.15682 9.99987 2.98725 9.92963 2.86223 9.8046C2.7372 9.67958 2.66696 9.51001 2.66696 9.3332ZM3.33363 11.3332C3.15682 11.3332 2.98725 11.4034 2.86223 11.5285C2.7372 11.6535 2.66696 11.8231 2.66696 11.9999C2.66696 12.1767 2.7372 12.3462 2.86223 12.4713C2.98725 12.5963 3.15682 12.6665 3.33363 12.6665H13.3336C13.5104 12.6665 13.68 12.5963 13.805 12.4713C13.9301 12.3462 14.0003 12.1767 14.0003 11.9999C14.0003 11.8231 13.9301 11.6535 13.805 11.5285C13.68 11.4034 13.5104 11.3332 13.3336 11.3332H3.33363ZM2.0043 5.8972C1.98478 6.1512 2.03844 6.40548 2.15892 6.62994C2.2794 6.85439 2.46166 7.03964 2.68414 7.16375C2.90661 7.28786 3.15998 7.34564 3.41426 7.33025C3.66855 7.31486 3.9131 7.22694 4.11898 7.0769C4.32486 6.92687 4.48345 6.72099 4.57598 6.48364C4.66851 6.24629 4.69111 5.9874 4.6411 5.73761C4.59109 5.48781 4.47057 5.25758 4.2938 5.07414C4.11703 4.8907 3.8914 4.76175 3.64363 4.70253C3.80296 4.44253 4.0463 4.1532 4.41963 3.85187C4.48774 3.79676 4.54433 3.72877 4.58617 3.65179C4.62801 3.5748 4.65427 3.49034 4.66347 3.4032C4.67266 3.31607 4.6646 3.22798 4.63975 3.14396C4.6149 3.05995 4.57374 2.98165 4.51863 2.91353C4.46352 2.84542 4.39553 2.78883 4.31855 2.74699C4.24157 2.70516 4.1571 2.67889 4.06997 2.6697C3.98284 2.66051 3.89474 2.66856 3.81073 2.69342C3.72671 2.71827 3.64841 2.75942 3.5803 2.81453C2.83563 3.41653 2.41563 4.04653 2.1983 4.63587C2.04937 5.03898 1.98383 5.46795 2.0043 5.8972Z" fill="#667085"/>
                                            </svg>
                                            &nbsp; Deactivate Quote
                                          </SoftButton>
                                        </SoftBox>
                                      ) : (
                                        <SoftBox>
                                            <SoftButton
                                              variant="outlined"
                                              sx={{
                                                "& svg": { fontSize: "1.25rem !important" },
                                                background: "linear-gradient(180deg, rgba(255, 255, 255, 0.5) 4.41%, rgba(237, 236, 242, 0.5) 94.12%)",
                                                borderRadius: "4px",
                                                border: "1px solid #EAECF0",
                                                padding: "10px 16px",
                                                color: workspaceBg === 'primary' ? 'white !important' : '#667085 !important',
                                                textTransform: "capitalize !important",
                                                zIndex: "1000!important",
                                                cursor: "pointer"
                                              }}
                                              onClick={handleActivateQuote}
                                            >
                                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                              <path fillRule="evenodd" clipRule="evenodd" d="M7.8523 2.91387C7.90737 2.982 7.94846 3.06033 7.97324 3.14436C7.99802 3.22839 8.006 3.31648 7.99671 3.40359C7.98742 3.49071 7.96106 3.57514 7.91912 3.65206C7.87718 3.72897 7.82049 3.79687 7.7523 3.85187C7.37963 4.1532 7.1363 4.44253 6.97696 4.70253C7.22467 4.76186 7.4502 4.89091 7.62687 5.07439C7.80354 5.25788 7.92396 5.48813 7.97387 5.73791C8.02379 5.98768 8.00111 6.24653 7.90853 6.48382C7.81595 6.72112 7.65733 6.92692 7.45145 7.0769C7.24557 7.22687 7.00104 7.31474 6.74679 7.3301C6.49254 7.34546 6.23921 7.28767 6.01677 7.16357C5.79433 7.03947 5.6121 6.85425 5.49162 6.62983C5.37115 6.4054 5.31748 6.15117 5.33696 5.8972C5.32096 5.5692 5.3503 5.13053 5.5323 4.63587C5.74963 4.04653 6.16963 3.41653 6.9143 2.81453C6.98243 2.75946 7.06076 2.71837 7.14479 2.69359C7.22882 2.66881 7.31691 2.66083 7.40402 2.67012C7.49114 2.67941 7.57557 2.70577 7.65249 2.74771C7.7294 2.78965 7.7973 2.84634 7.8523 2.91453V2.91387ZM10.0003 3.3332C9.82349 3.3332 9.65392 3.40344 9.52889 3.52846C9.40387 3.65349 9.33363 3.82306 9.33363 3.99987C9.33363 4.17668 9.40387 4.34625 9.52889 4.47127C9.65392 4.5963 9.82349 4.66653 10.0003 4.66653H13.3336C13.5104 4.66653 13.68 4.5963 13.805 4.47127C13.9301 4.34625 14.0003 4.17668 14.0003 3.99987C14.0003 3.82306 13.9301 3.65349 13.805 3.52846C13.68 3.40344 13.5104 3.3332 13.3336 3.3332H10.0003ZM10.0003 5.99987C9.82349 5.99987 9.65392 6.0701 9.52889 6.19513C9.40387 6.32015 9.33363 6.48972 9.33363 6.66653C9.33363 6.84334 9.40387 7.01291 9.52889 7.13794C9.65392 7.26296 9.82349 7.3332 10.0003 7.3332H13.3336C13.5104 7.3332 13.68 7.26296 13.805 7.13794C13.9301 7.01291 14.0003 6.84334 14.0003 6.66653C14.0003 6.48972 13.9301 6.32015 13.805 6.19513C13.68 6.0701 13.5104 5.99987 13.3336 5.99987H10.0003ZM2.66696 9.3332C2.66696 9.15639 2.7372 8.98682 2.86223 8.8618C2.98725 8.73677 3.15682 8.66653 3.33363 8.66653H13.3336C13.5104 8.66653 13.68 8.73677 13.805 8.8618C13.9301 8.98682 14.0003 9.15639 14.0003 9.3332C14.0003 9.51001 13.9301 9.67958 13.805 9.8046C13.68 9.92963 13.5104 9.99987 13.3336 9.99987H3.33363C3.15682 9.99987 2.98725 9.92963 2.86223 9.8046C2.7372 9.67958 2.66696 9.51001 2.66696 9.3332ZM3.33363 11.3332C3.15682 11.3332 2.98725 11.4034 2.86223 11.5285C2.7372 11.6535 2.66696 11.8231 2.66696 11.9999C2.66696 12.1767 2.7372 12.3462 2.86223 12.4713C2.98725 12.5963 3.15682 12.6665 3.33363 12.6665H13.3336C13.5104 12.6665 13.68 12.5963 13.805 12.4713C13.9301 12.3462 14.0003 12.1767 14.0003 11.9999C14.0003 11.8231 13.9301 11.6535 13.805 11.5285C13.68 11.4034 13.5104 11.3332 13.3336 11.3332H3.33363ZM2.0043 5.8972C1.98478 6.1512 2.03844 6.40548 2.15892 6.62994C2.2794 6.85439 2.46166 7.03964 2.68414 7.16375C2.90661 7.28786 3.15998 7.34564 3.41426 7.33025C3.66855 7.31486 3.9131 7.22694 4.11898 7.0769C4.32486 6.92687 4.48345 6.72099 4.57598 6.48364C4.66851 6.24629 4.69111 5.9874 4.6411 5.73761C4.59109 5.48781 4.47057 5.25758 4.2938 5.07414C4.11703 4.8907 3.8914 4.76175 3.64363 4.70253C3.80296 4.44253 4.0463 4.1532 4.41963 3.85187C4.48774 3.79676 4.54433 3.72877 4.58617 3.65179C4.62801 3.5748 4.65427 3.49034 4.66347 3.4032C4.67266 3.31607 4.6646 3.22798 4.63975 3.14396C4.6149 3.05995 4.57374 2.98165 4.51863 2.91353C4.46352 2.84542 4.39553 2.78883 4.31855 2.74699C4.24157 2.70516 4.1571 2.67889 4.06997 2.6697C3.98284 2.66051 3.89474 2.66856 3.81073 2.69342C3.72671 2.71827 3.64841 2.75942 3.5803 2.81453C2.83563 3.41653 2.41563 4.04653 2.1983 4.63587C2.04937 5.03898 1.98383 5.46795 2.0043 5.8972Z" fill="#667085"/>
                                              </svg>
                                              &nbsp; Activate Quote
                                            </SoftButton>
                                        </SoftBox>
                                      )}
                                  </SoftBox>
                                  <SoftBox sx={{display: 'flex', justifyContent: 'space-between', marginTop: '1.5rem'}}>
                                    <SoftButton
                                      color="white"
                                      onClick={handleCloseModal}
                                      sx={{
                                        background: "rgba(255, 255, 255, 0.1)",
                                        minWidth: "175px",
                                        borderRadius: "3px",
                                        color: "#FFFFFF"
                                      }}
                                    >
                                      Cancel
                                    </SoftButton>
                                    <SoftButton
                                      color="white"
                                      onClick={submitImage}
                                      sx={{
                                        background: '#FFFFFF',
                                        minWidth: "175px",
                                        color: '#344054',
                                        borderRadius: "3px"
                                      }}
                                    >
                                      Update
                                    </SoftButton>
                                  </SoftBox>
                                </SoftBox>
                              </SoftBox>
                            </Fade>
                          </Modal>
                          </>
                          )}
                        </>
                    )}
                    </SoftBox>
                  </SoftBox>
                </Box>
              </div>
              <SoftBox
                overflow="hidden"
                position="absolute"
                left="15px"
                sx={{
                  height: { mini: "440px", xxs: "480px", lg: "530px" },
                  display: "flex",
                  justifyContent: "flex-end",
                  right: isSmallScreen ? "50px" : "150px",
                  zIndex: "-1000!important",
                  ...(screenWidth <= 1280 && ({
                    right: "100px"
                  }))
                }}
              >
                <SoftBox
                  sx={{
                    maxWidth: {
                      mini: "50px",
                      xs: "80px",
                      md: "100px",
                      xxl: "120px",
                    },
                    display: "inline-block",
                    position: "relative",
                    width: "full",
                  }}
                >
                  <img
                    src={GmbWelcomeLogo}
                    alt=""
                    style={{ animation: `70s scrollEffect infinite linear` }}
                  />
                </SoftBox>
              </SoftBox>
            </SoftBox>
          </SoftBox>
        </Grid>
        <Grid item mini={12} md={12} lg={3}>
          <CustomWidthTooltip 
            title={<CustomTooltipContent closeTooltip={hideTooltip} onNextTooltip={onNextTooltip} />}
            placement="left"
            arrow
            open={openTooltip}
          >
            <SoftBox
              sx={{
                height: { mini: "835px", xxs: "875px", md: "625px" },
                padding: { mini: "0.5rem", xxs: "1.5rem 1.5rem 1.5rem 0rem" },
                paddingTop: { md: "32px" },
                ...(isMidsizeScreen && ({
                  padding: "1.5rem 1.5rem 0 1.5rem !important",
                  minHeight: "1px !important",
                  height: "unset"
                }))
              }}
            >
              <SoftBox
                sx={{
                  background: "#fff",
                  paddingTop: { mini: "101.25px", xs: "109.25px", md: "75px" },
                  paddingBottom: "2rem",
                  overflowX: "hidden",
                  borderRadius: "12px",
                  height: "100%",
                  maxHeight: { mini: "960px", xxs: "1000px", md: "850px" },
                  ...(isMidsizeScreen && ({ maxHeight: "unset" }))
                }}
              >
                <SoftBox sx={{ padding: { mini: "8px 16px 0 16px", sm: "0 2rem 0 2rem" }, marginTop: { mini: "-6rem", sm: "-5rem", md: "-3rem" } }}>
                  <SoftBox sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <SoftTypography
                      sx={{
                        textFillColor: 'primary.main',
                        color: "#000000",
                        fontSize: { mini: "14px", sm: "20px" },
                        lineHeight: { mini: "18px", sm: "24px" },
                        fontWeight: { mini: 500, sm: 600 }
                      }}
                    >
                      My Widgets
                    </SoftTypography>
                    <AddIcon sx={{height: "20px", width: "20px", cursor: 'pointer'}} onClick={() => navigate("/widgets")} />
                  </SoftBox>
                  {!isMidsizeScreen && <hr className="my-3 divide-slate-300" />}
                </SoftBox>

                {isMidsizeScreen && <hr className="my-3 divide-slate-300" />}

                <Box
                  sx={{
                    marginX: "auto",
                    width: "100%",
                    paddingX: { mini: "0.5rem", md: "1rem", xl: "2rem" },
                    ...(isMidsizeScreen && ({ paddingY: "16px" })),
                    flex: 1,
                    // display: "grid",
                    alignItems: "center",
                    position: "relative",
                  }}
                >
                  {userWidget && userWidget.length ? (
                    userWidget.slice(0, 3).map((widget) => (
                      <div
                        key={widget.key}
                        style={{ ...(isMidsizeScreen && ({ padding: "0 1rem 1.5rem 1rem" })) }}
                      >
                        <DynamicComponent
                          widget={widget}
                          showPerformBox={true}
                          key={widget.key}
                          showGraph={false}
                          isSmallScreen={isMidsizeScreen}
                        />
                      </div>
                    ))
                  ) : (
                    DefaultWidgets.map((DefaultWidget) => (
                      <div
                        key={DefaultWidget.index}
                        style={{ ...(isMidsizeScreen && ({ padding: "0 1rem 1.5rem 1rem" })) }}
                      >
                        <DefaultWidget.Widget showPerformBox={true} isSmallScreen={isMidsizeScreen} />
                      </div>
                    ))
                  )}
                  <Grid container sx={{ marginTop: { mini: "1rem", lg: "3rem" } }}>
                    <Grid item mini={12} style={{ ...(isMidsizeScreen && ({ paddingRight: "20px" })),
                    display: 'flex', 
                    justifyContent: 'flex-end'
                    }}>
                      <Tooltip title="Boost your company's performance with our custom widgets, designed to streamline operations and enhance efficiency." placement="bottom-end">
                        <SoftTypography sx={{ color: "#000000", fontSize: "12px", lineHeight: "24px", fontWeight: "500" }}>
                          {isMidsizeScreen ? <span>&nbsp;</span> : "What is a widget?"}
                        </SoftTypography>
                      </Tooltip>
                    </Grid>
                  </Grid>
                </Box>
              </SoftBox>
            </SoftBox>
          </CustomWidthTooltip>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Hero;


